import React from 'react';
import styled, {css} from 'styled-components';
import Slider from 'react-slick';
import {getColors, maxMediaQueries, mediaQueries} from '../utils/styling';
import Col from '../atoms/Col';
import Hidden from '../atoms/Hidden';
import {H2, P} from '../atoms/TextComponents';
import {Grid} from 'react-styled-flexboxgrid';

const data = {};
// sets the padding around the active slides, making room to show the inactive ones
const centerPadding = {
  'default': '90px',
  'md': '60px',
  'sm': '40px',
  'xs': '10px'
};

/* region Styled Components */

const SliderContainer = styled.div`
  position: relative;

  margin-left: -2rem;
  margin-right: -2rem;

  ${mediaQueries.sm`
    margin-left: -${centerPadding.sm};
    margin-right: -${centerPadding.sm};
  `};
        
  ${mediaQueries.md`
    margin-left: -${centerPadding.md};
    margin-right: -${centerPadding.md};
  `};
  
  ${mediaQueries.lg`
    margin-left: -${centerPadding.default};
    margin-right: -${centerPadding.default};
  `};
    
  .testimonial-slick-slider .slick-track {
    height: 368px;
  }
  
  .testimonial-slick-slider .slick-slide > div {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    height: 100%;
    
    ${maxMediaQueries.sm`
        padding: 8px;
     `};
  }
`;

const SliderOverlay = styled.div`
  position: absolute;
  top: 0;  
  right: ${({position}) => position === 'right' ? '0' : 'initial'};
  bottom: 0;
  left:  ${({position}) => position === 'left' ? '0' : 'initial'};
  z-index: 1;
  width: ${centerPadding.xs};
  
  ${
    ({position, variant, theme}) => {
      if (variant === 'dark') {
        return css`background: linear-gradient(${({position, theme}) => position === 'left' ?
            'to right' :
            'to left'}, rgba(45, 55, 72, 1) 0%, rgba(45, 55, 72, 0.4) 55%, rgba(45, 55, 72, 0));`;
      } else {
        return css`background: linear-gradient(${({position, theme}) => position === 'left' ?
            'to right' :
            'to left'}, rgba(247, 250, 252, 1) 0%, rgba(247, 250, 252, 0.4) 55%, rgba(247, 250, 252, 0));`;
      }
    }
  }
  
   @media(min-width: 425px) {
    width: ${centerPadding.sm};
   };
  
   ${mediaQueries.md`
    width: ${centerPadding.md};
  `};
  
   ${mediaQueries.lg`
    width: ${centerPadding.lg};
  `};
`;

const NavButtonWrapper = styled.div`
  position: absolute;
  top: 0;  
  right: ${({position}) => position === 'right' ? '50px' : 'initial'};
  bottom: 0;
  left:  ${({position}) => position === 'left' ? '50px' : 'initial'};
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  
  
  
  ${maxMediaQueries.sm`
    display: none;
  `};
`;

const NavButton = styled.button`
  border: none;
  border-radius: 4px;
  padding: 10px;
  width: 40px;
  height: 40px;
  ${({theme, variant}) => css`background: ${variant === 'dark' ? theme.colors.gray500 : theme.colors.gray600}`};
  outline: none;
  cursor: pointer;
  
  svg {
    ${({theme, variant}) => css`fill: ${variant === 'dark' ? theme.colors.gray100 : theme.colors.gray100}`};
  }
`;

const Circle = styled.div`
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 4px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid;
  ${({theme}) => css`border-color: ${getColors(theme).gray600}`};
  ${({active, theme}) => (active ? css`background-color: ${getColors(theme).gray600}` : '')};
`;

const CirclesOuter = styled.div`
  display: flex;
  align-content: center;
  justify-content: center;
  margin: 16px 0;
  transform: translateY(-40px);
  
  ${maxMediaQueries.lg`
    transform: none;
    margin: 30px 0 0px;
  `}
`;

const Circles = ({iterable, active, onClick}) => (
    <CirclesOuter>
      {iterable.map((i, idx) => (
          <Circle onClick={() => onClick(i)} active={i == active} key={idx}/>
      ))}
    </CirclesOuter>
);

const TestimonialContent = styled.div`
  position: relative;
  display: flex;
  align-items: start;
  flex-direction: column;
  border-radius: 4px;
  padding: 16px 32px;
  width: 100%;
  max-width: 480px;
  height: 100%;
  min-height: 296px;
  background-color: #ffffff;
  color: #606060;
  box-shadow: 0 4px 16px rgba(74, 85, 104, 0.1);
  
  .testimonial-quote {
    flex: 1;
    margin-bottom: 16px;
  }

  .job-title {
    opacity: 0.8;
  }
`;

const CompanyLogo = styled.img`
  max-width: 80%;
  width: auto;
  height: 56px;
  margin-bottom: 24px;
`;

/* endregion */

/* region TESTIMONIAL */

const Testimonial = ({
                       content,
                       authorFirstName,
                       authorLastName,
                       authorJobTitle,
                       authorCompany
                     }) => {
  return (
      <TestimonialContent>
        {authorCompany && authorCompany.balancedLogoColor ?
            <CompanyLogo src={authorCompany.balancedLogoColor.file.url}/>
            : null
        }
        <P className="testimonial-quote"><i>{content}</i></P>
        <Col>
          <P>
            <strong>
              {authorFirstName} {authorLastName}
            </strong>
          </P>
          {authorCompany ? <span className="job-title">{authorJobTitle}</span> : null}
        </Col>
      </TestimonialContent>
  );
};

/* endregion */

/* region TESTIMONIAL SLIDER */

class TestimonialSlider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {active: 0, order: [], data: []};
  }

  componentDidMount() {
    const {data} = this.props;
    const count = data.length;
    const order = Object.keys(data).reduce(
        (obj, value) => ({
          ...obj,
          [value]: Number(value) + 1 < count ? Number(value) + 1 : 0
        }),
        {}
    );
    this.setState({data, count, order});
  }

  navigateToPrevOrNext = (val) => {
    if (this.slider) {
      if (val > -1) {
        this.slider.slickNext();
      } else {
        this.slider.slickPrev();
      }
    }
  };

  choose = i => {
    if (this.slider) {
      this.slider.slickGoTo(i);
      this.setState({active: i});
    }
  };

  render() {
    const {active, order, data} = this.state;

    if (data) {
      const settings = {
        infinite: true,
        slidesToShow: 3,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: centerPadding.default,
        autoplay: false, //true,
        autoplaySpeed: 6000,
        speed: 1000,
        arrows: false,
        className: 'testimonial-slick-slider',
        afterChange: current => this.setState({active: current}),
        responsive: [
          {
            breakpoint: 1024,
            settings: {
              slidesToShow: 2,
              centerPadding: centerPadding.md
            }
          },
          {
            breakpoint: 768,
            settings: {
              slidesToShow: 1,
              centerPadding: centerPadding.sm
            }
          },
          {
            breakpoint: 425,
            settings: {
              slidesToShow: 1,
              centerPadding: centerPadding.xs
            }
          }
        ]
      };
      return (
          <Grid>

            <H2 className="text-center">
              {this.props.title}
            </H2>

            <SliderContainer>
              <SliderOverlay position="left" variant={this.props.variant}>
                <NavButtonWrapper position="left">
                  <NavButton onClick={() => this.navigateToPrevOrNext(-1)} variant={this.props.variant}>
                    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path
                          d="M20 11.25H4.81263L11.8127 18.2501L10 20L0 10L10 0L11.7498 1.74994L4.81263 8.75H20V11.25Z"/>
                    </svg>
                  </NavButton>
                </NavButtonWrapper>
              </SliderOverlay>

              <Slider ref={el => (this.slider = el)} {...settings}>
                {data.map((i, idx) => (
                    <Testimonial key={idx} indx={i} {...i} />
                ))}
              </Slider>

              <SliderOverlay position="right" variant={this.props.variant}>
                <NavButtonWrapper position="right">
                  <NavButton onClick={() => this.navigateToPrevOrNext(1)} variant={this.props.variant}>
                    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path
                          d="M0 11.25H15.1874L8.18725 18.2501L10 20L20 10L10 0L8.25018 1.74994L15.1874 8.75H0V11.25Z"/>
                    </svg>
                  </NavButton>
                </NavButtonWrapper>
              </SliderOverlay>

              <Hidden sm>
                <Circles
                    onClick={this.choose}
                    iterable={Object.keys(order)}
                    active={active}
                />
              </Hidden>
            </SliderContainer>

          </Grid>
      );
    } else {
      return null;
    }
  }
}

/* endregion */

export default TestimonialSlider;
